import { Component, OnInit, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { environment } from '../core/environment';

interface Lookup { id: string; name: string; }
interface GuestBooking {
  id: string;
  guestPassCode: string;
  guestName: string;
  mobile: string;
  email?: string;
  sportName: string;
  durationMinutes: number;
  charges: number;
  paymentStatus: number;
  status: string;
  bookingDate: string;
  slotStartTime: string;
  slotEndTime: string;
}

interface GuestQrPass {
  bookingId: string;
  guestName: string;
  email: string;
  cardNumber: string;
  scanUrl: string;
  qrSvg: string;
  emailSent: boolean;
  emailMessage: string;
}

interface GuestPaymentQr {
  bookingId: string;
  guestName: string;
  amount: number;
  upiLink: string;
  qrSvg: string;
}

@Component({
  selector: 'app-guest-booking',
  standalone: true,
  imports: [ReactiveFormsModule],
  template: `
    <div class="guest-desk">
      <section class="guest-desk-header">
        <div>
          <span class="eyebrow">Walk-in operations</span>
          <h2>Guest Play Management</h2>
          <p>Streamlined entry, payment QR and session tracking for walk-in athletes.</p>
        </div>
        <div class="guest-total-card">
          <span>Today's Total</span>
          <strong>{{ guests().length }} Guests</strong>
        </div>
      </section>

      <section class="guest-workspace">
        <div class="guest-left-rail">
          <form class="guest-entry-card" [formGroup]="form" (ngSubmit)="create()">
            <div class="guest-card-title">
              <div>
                <h3>New Guest Entry</h3>
                <p>Generate booking, collect payment and issue pass.</p>
              </div>
              <span>Guest+</span>
            </div>

            <label>Full Name
              <input formControlName="guestName" placeholder="e.g. John Smith">
            </label>

            <div class="two-col">
              <label>Sport
                <select formControlName="sportId">
                  @for (sport of sports(); track sport.id) {
                    <option [value]="sport.id">{{ sport.name }}</option>
                  }
                </select>
              </label>
              <label>Duration
                <select formControlName="durationMinutes">
                  <option [value]="60">1 Hour</option>
                  <option [value]="90">1.5 Hours</option>
                  <option [value]="120">2 Hours</option>
                </select>
              </label>
            </div>

            <label>Contact Number
              <input formControlName="mobile" placeholder="+91 98765 43210">
            </label>

            <label>Email for QR Pass
              <input type="email" formControlName="email" placeholder="guest@example.com">
            </label>

            <div class="two-col">
              <label>Date
                <input type="date" formControlName="bookingDate">
              </label>
              <label>Start Time
                <input type="time" formControlName="slotStartTime">
              </label>
            </div>

            <label class="check-row guest-paid">
              <input type="checkbox" formControlName="markPaid"> Mark payment as paid
            </label>

            <button class="guest-submit" type="submit" [disabled]="form.invalid">Generate & Check-In</button>
          </form>

          <article class="digital-pass-card">
            <div class="digital-pass-heading">
              <h3>Digital Guest Pass</h3>
              <span>Secure</span>
            </div>

            @if (paymentQr()) {
              <div class="ticket-shell">
                <div class="ticket-brand">
                  <img src="assets/perks-logo.svg" alt="PERKS Sports Academy">
                  <div>
                    <strong>PAYMENT QR</strong>
                    <span>UPI Gateway</span>
                  </div>
                </div>
                <div class="ticket-qr" [innerHTML]="safePaymentQrSvg()"></div>
                <h4>Pay Rs {{ paymentQr()?.amount }}</h4>
                <p>{{ paymentQr()?.guestName }}</p>
                @if (paymentMessage()) {
                  <small class="ticket-message">{{ paymentMessage() }}</small>
                }
                <button type="button" (click)="markPaid(paymentQr()!.bookingId)">Mark Paid</button>
              </div>
            } @else if (qrPass()) {
              <div class="ticket-shell">
                <div class="ticket-brand">
                  <img src="assets/perks-logo.svg" alt="PERKS Sports Academy">
                  <div>
                    <strong>ELITE ACADEMY</strong>
                    <span>Valid Today Only</span>
                  </div>
                </div>
                <div class="ticket-qr" [innerHTML]="safeQrSvg()"></div>
                <h4>{{ qrPass()?.guestName }}</h4>
                <p>{{ qrPass()?.cardNumber }}</p>
                <small class="ticket-message" [class.good]="qrPass()?.emailSent">{{ qrPass()?.emailMessage }}</small>
                <div class="ticket-actions">
                  <a [href]="qrPass()?.scanUrl" target="_blank" rel="noreferrer">Open Pass</a>
                </div>
              </div>
            } @else {
              <div class="ticket-shell empty">
                <div class="ticket-brand">
                  <img src="assets/perks-logo.svg" alt="PERKS Sports Academy">
                  <div>
                    <strong>GUEST PASS</strong>
                    <span>Ready to issue</span>
                  </div>
                </div>
                <div class="ticket-placeholder">QR</div>
                <h4>Waiting for booking</h4>
                <p>QR pass will appear here after guest entry.</p>
              </div>
            }
          </article>
        </div>

        <article class="guest-activity-card">
          <div class="guest-activity-header">
            <div>
              <h3>Today's Activity</h3>
              <span>Live</span>
            </div>
            <div class="guest-toolbar">
              <button type="button" (click)="load()">Refresh</button>
              <button class="dark" type="button">Filter</button>
            </div>
          </div>

          <div class="guest-table-wrap">
            <table class="guest-table">
              <thead>
                <tr>
                  <th>Guest Details</th>
                  <th>Sport & Slot</th>
                  <th>Payment</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                @for (guest of guests(); track guest.id) {
                  <tr>
                    <td>
                      <div class="guest-person">
                        <span>{{ initials(guest.guestName) }}</span>
                        <div>
                          <strong>{{ guest.guestName }}</strong>
                          <small>{{ guest.mobile }} | {{ guest.email || 'No email' }}</small>
                        </div>
                      </div>
                    </td>
                    <td>
                      <strong>{{ guest.sportName }}</strong>
                      <small>{{ guest.slotStartTime }} - {{ guest.slotEndTime }} | {{ guest.durationMinutes }} min</small>
                    </td>
                    <td>
                      <span class="guest-pill" [class.paid]="guest.paymentStatus === 3">
                        {{ guest.paymentStatus === 3 ? 'Paid' : 'Pending' }}
                      </span>
                      <small>Rs {{ guest.charges }}</small>
                    </td>
                    <td>
                      <span class="guest-pill status">{{ displayStatus(guest.status) }}</span>
                      <small>{{ guest.guestPassCode }}</small>
                    </td>
                    <td>
                      <div class="guest-actions">
                        <button class="pay" type="button" (click)="makePayment(guest.id)">Pay</button>
                        <button type="button" (click)="checkIn(guest.id)">Check-In</button>
                        <button class="dark" type="button" (click)="checkOut(guest.id)">Check-Out</button>
                        <button class="ghost" type="button" (click)="generateQr(guest.id)">QR</button>
                      </div>
                    </td>
                  </tr>
                } @empty {
                  <tr>
                    <td colspan="5">
                      <div class="guest-empty">No guest bookings for today. Create the first walk-in entry.</div>
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>

          <div class="guest-table-footer">
            <span>Showing {{ guests().length }} entries today</span>
            <div>
              <button type="button">Prev</button>
              <button type="button">Next</button>
            </div>
          </div>
        </article>
      </section>

      <section class="guest-insights">
        <article><span>Peak Hour</span><strong>5:00 PM</strong><p>+12% traffic</p></article>
        <article><span>Checked In</span><strong>{{ countStatus('CheckedIn') }}</strong><p>Currently inside</p></article>
        <article><span>Revenue Today</span><strong>Rs {{ revenue() }}</strong><p>Secure gateway</p></article>
        <article><span>Paid Bookings</span><strong>{{ paidCount() }}</strong><p>Payment completed</p></article>
      </section>

      @if (guests().length) {
        <button class="floating-qr" type="button" (click)="generateQr(guests()[0].id)">QR</button>
      }
    </div>
  `
})
export class GuestBookingComponent implements OnInit {
  private readonly http = inject(HttpClient);
  private readonly fb = inject(FormBuilder);
  private readonly sanitizer = inject(DomSanitizer);
  readonly sports = signal<Lookup[]>([]);
  readonly guests = signal<GuestBooking[]>([]);
  readonly qrPass = signal<GuestQrPass | null>(null);
  readonly safeQrSvg = signal<SafeHtml>('');
  readonly paymentQr = signal<GuestPaymentQr | null>(null);
  readonly safePaymentQrSvg = signal<SafeHtml>('');
  readonly paymentMessage = signal('');
  private readonly fallbackSports: Lookup[] = [
    { id: '30000000-0000-0000-0000-000000000001', name: 'Swimming' },
    { id: '30000000-0000-0000-0000-000000000002', name: 'Cricket' },
    { id: '30000000-0000-0000-0000-000000000003', name: 'Football' },
    { id: '30000000-0000-0000-0000-000000000004', name: 'Badminton' },
    { id: '30000000-0000-0000-0000-000000000005', name: 'Skating' },
    { id: '30000000-0000-0000-0000-000000000006', name: 'Tennis' },
    { id: '30000000-0000-0000-0000-000000000007', name: 'Karate' },
    { id: '30000000-0000-0000-0000-000000000008', name: 'Yoga' }
  ];
  readonly form = this.fb.nonNullable.group({
    guestName: ['', Validators.required],
    mobile: ['', Validators.required],
    email: ['', [Validators.required, Validators.email]],
    sportId: ['', Validators.required],
    bookingDate: [new Date().toISOString().slice(0, 10), Validators.required],
    slotStartTime: ['18:00:00', Validators.required],
    durationMinutes: [60, Validators.required],
    markPaid: [true]
  });

  ngOnInit(): void {
    this.setSports(this.fallbackSports);
    this.http.get<Lookup[]>(`${environment.apiUrl}/masters/sports`).subscribe({
      next: (sports) => this.setSports(sports.length ? sports : this.fallbackSports),
      error: () => this.setSports(this.fallbackSports)
    });
    this.load();
  }

  countStatus(status: string): number { return this.guests().filter((x) => x.status === status).length; }
  paidCount(): number { return this.guests().filter((x) => x.paymentStatus === 3).length; }
  revenue(): number { return this.guests().filter((x) => x.paymentStatus === 3).reduce((sum, x) => sum + x.charges, 0); }

  create(): void {
    if (this.form.invalid) return;
    const value = this.form.getRawValue();
    const payload = {
      ...value,
      slotStartTime: this.normalizeTime(value.slotStartTime)
    };
    this.http.post<GuestBooking>(`${environment.apiUrl}/guest-bookings`, payload).subscribe((booking) => {
      this.load();
      this.generateQr(booking.id);
    });
  }

  checkIn(id: string): void { this.http.post(`${environment.apiUrl}/guest-bookings/${id}/check-in`, {}).subscribe(() => this.load()); }
  checkOut(id: string): void { this.http.post(`${environment.apiUrl}/guest-bookings/${id}/check-out`, {}).subscribe(() => this.load()); }
  makePayment(id: string): void {
    this.paymentMessage.set('');
    this.http.post<GuestPaymentQr>(`${environment.apiUrl}/guest-bookings/${id}/payment-qr`, {}).subscribe({
      next: (qr) => {
        this.qrPass.set(null);
        this.paymentQr.set(qr);
        this.safePaymentQrSvg.set(this.sanitizer.bypassSecurityTrustHtml(qr.qrSvg));
      },
      error: (error) => this.paymentMessage.set(error.error?.message ?? 'Unable to generate UPI payment QR.')
    });
  }

  markPaid(id: string): void {
    this.http.post(`${environment.apiUrl}/guest-bookings/${id}/mark-paid`, {}).subscribe(() => {
      this.paymentMessage.set('Payment marked as paid. Entry QR is being generated.');
      this.load();
      this.generateQr(id);
    });
  }

  generateQr(id: string): void {
    this.http.post<GuestQrPass>(`${environment.apiUrl}/guest-bookings/${id}/qr-pass`, {}).subscribe((pass) => {
      this.paymentQr.set(null);
      this.qrPass.set(pass);
      this.safeQrSvg.set(this.sanitizer.bypassSecurityTrustHtml(pass.qrSvg));
    });
  }

  load(): void {
    this.http.get<GuestBooking[]>(`${environment.apiUrl}/guest-bookings/today`).subscribe((items) => this.guests.set(items));
  }

  initials(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    return ((parts[0]?.[0] ?? 'G') + (parts[1]?.[0] ?? '')).toUpperCase();
  }

  displayStatus(status: string): string {
    return status.replace(/([a-z])([A-Z])/g, '$1 $2');
  }

  private normalizeTime(value: string): string {
    return value.length === 5 ? `${value}:00` : value;
  }

  private setSports(sports: Lookup[]): void {
    this.sports.set(sports);
    if (!this.form.controls.sportId.value) {
      const swimming = sports.find((sport) => sport.name === 'Swimming') ?? sports[0];
      this.form.patchValue({ sportId: swimming?.id ?? '' });
    }
  }
}
